import * as React from "react";

import type { EventRecord } from "@/shared/domain/events";
import { PlotPanelState } from "@/shared/state/visualization";

import {
  useEphemeralWorkspaceState,
  useWorkspaceSources,
} from "../../../WorkspaceCtx/hooks";
import { useWorkspaceEvents } from "../../../WorkspaceCtx/useWorkspaceEvents";
import { isEventAnchored, SeriesAlignment } from "../alignment";
import { resolveSeriesAlignment } from "../resolveSeriesAlignment";

/**
 * The events an event-anchored alignment resolves against, or `undefined` while
 * the workspace's events are still loading and the panel must hold.
 *
 * A failed fetch resolves against no events rather than holding forever. Both
 * states leave the query's `data` undefined, so telling them apart takes the
 * error flag. Holding on a failure would leave the panel reporting itself
 * aligned (`isAligned` reads config, not this) while it plots absolute time,
 * with sync and event markers disabled and no way back. Resolving instead bases
 * every series at its own recording's start and flags `isFallback`, which the
 * legend and tooltip already explain to the user.
 */
export function anchorEvents(query: {
  data: EventRecord[] | undefined;
  isError: boolean;
}): EventRecord[] | undefined {
  if (query.data !== undefined) {
    return query.data;
  }
  return query.isError ? [] : undefined;
}

/**
 * The panel's aligned per-series bases, or `undefined` when the panel plots
 * absolute time (`config.xAxisAlignment` unset).
 *
 * An event-anchored session alignment also resolves to `undefined` until the
 * workspace's events have loaded: computing bases from a not-yet-loaded event
 * list would flag every series as missing its anchor and load high-detail data
 * against bases that change as soon as the events arrive. If that fetch fails
 * the panel resolves anyway, against no events; see {@link anchorEvents}. Every
 * other alignment (session-start, first-data, per-series) resolves from
 * workspace state alone, with no fetch to wait on.
 *
 * The memo keys on the alignment object by identity. `setPlotPanelConfig`
 * merges its patch shallowly over the existing config, so a write to any
 * other config key leaves the same alignment object in place.
 */
export function useSeriesAlignment(
  state: PlotPanelState,
): SeriesAlignment | undefined {
  const alignment = state.config?.xAxisAlignment;
  const sources = useWorkspaceSources();
  const { topics } = useEphemeralWorkspaceState();
  // Deduplicated by query key with the workspace's other useWorkspaceEvents
  // callers, so asking for the events here adds no fetch.
  const eventsQuery = useWorkspaceEvents();
  const eventAnchored = isEventAnchored(alignment);
  // Only an event anchor reads events. Keying the memo on them for the other
  // alignments would hand every consumer a new alignment object on every
  // unrelated event edit, with nothing about the alignment changed.
  const events = eventAnchored ? anchorEvents(eventsQuery) : undefined;

  return React.useMemo(() => {
    if (alignment === undefined || (eventAnchored && events === undefined)) {
      return undefined;
    }
    return resolveSeriesAlignment({
      series: state.data,
      sources,
      alignment,
      events: events ?? [],
      topics,
    });
  }, [alignment, eventAnchored, state.data, sources, events, topics]);
}
